import { EMAIL_REGEX, POSTAL_CODE_REGEX, RFC_REGEX } from '@/constants/regex';
import { useGetBuyerFiscalData } from '@/hooks/buyers/useGetBuyerFiscalData';
import { buyerFiscalDataResponseToFiscalDataUi } from '@/hooks/buyers/useGetBuyerFiscalData/mapper';
import { useUpdateBuyerFiscalData } from '@/hooks/buyers/useUpdateBuyerFiscalData';
import { useGetCfdiUses } from '@/hooks/fiscal/useGetCfdiUses';
import { cfdiUsesResponseToSelectOptionsUi } from '@/hooks/fiscal/useGetCfdiUses/mapper';
import { useGetFiscalRegimes } from '@/hooks/fiscal/useGetFiscalRegimes';
import { fiscalRegimensResponseToSelectOptionsUi } from '@/hooks/fiscal/useGetFiscalRegimes/mapper';
import { SelectChangeEvent } from '@mui/material';
import { WritableDraft } from 'immer';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { FiscalDataUi, initialState } from './FiscalDataUi';

export function useFiscalDataViewController() {
  const [uiState, setUiState] = useImmer<FiscalDataUi>(initialState);
  const buyerId = useParams().buyerId;
  const { data: fiscalData } = useGetBuyerFiscalData(buyerId);
  const savedDataUiRef = useRef<FiscalDataUi>(undefined);

  const fiscalRegimesHook = useGetFiscalRegimes();
  const cfdiUsesHook = useGetCfdiUses();
  const updateFiscalDataHook = useUpdateBuyerFiscalData();

  const onChangeTaxInvoiceRequired = (newValue: boolean) => {
    if (savedDataUiRef.current?.taxInvoiceRequired === undefined) return;
    if (!buyerId) return;
    updateFiscalDataHook.updateRequiresTaxInvoice(buyerId, newValue);
  };

  const onChangedRfc = (newRfc: string) =>
    setUiState((draft) => {
      draft.rfc = newRfc.toUpperCase();
      draft.rfcError = RFC_REGEX.test(draft.rfc) ? undefined : 'RFC no válido';
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const handleFiscalRegimeChange = (event: SelectChangeEvent) =>
    setUiState((draft) => {
      draft.fiscalRegime = event.target.value;
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const onChangedLegalName = (newLegalName: string) =>
    setUiState((draft) => {
      draft.legalName = newLegalName;
      draft.legalNameError = draft.legalName.trim()
        ? undefined
        : 'Nombre legal inválido';
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const handleCfdiUseChange = (event: SelectChangeEvent) =>
    setUiState((draft) => {
      draft.cfdiUse = event.target.value;
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const onChangedPostalCode = (newPostalCode: string) =>
    setUiState((draft) => {
      draft.postalCode = newPostalCode;
      const isPostalCode = POSTAL_CODE_REGEX.test(draft.postalCode);
      draft.postalCodeError = isPostalCode
        ? undefined
        : 'Código postal inválido';
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const onChangedEmail = (newEmail: string) =>
    setUiState((draft) => {
      draft.taxEmail = newEmail;
      draft.taxEmailError = EMAIL_REGEX.test(draft.taxEmail)
        ? undefined
        : 'Correo inválido';
      draft.cancelDisabled = false;
      calculateContinueState(draft);
    });

  const calculateContinueState = (draft: WritableDraft<FiscalDataUi>) => {
    draft.saveDisabled =
      !draft.legalName.trim() ||
      !POSTAL_CODE_REGEX.test(draft.postalCode) ||
      !RFC_REGEX.test(draft.rfc) ||
      !EMAIL_REGEX.test(draft.taxEmail);
  };

  const onClickedCancel = () =>
    setUiState(savedDataUiRef.current ?? initialState);

  const onClickedSave = () => {
    if (!buyerId) return;
    updateFiscalDataHook.updateFiscalData(buyerId, uiState);
  };

  useEffect(() => {
    if (!fiscalData) return;
    const newUiState = buyerFiscalDataResponseToFiscalDataUi(
      uiState,
      fiscalData
    );
    savedDataUiRef.current = newUiState;
    setUiState(newUiState);
  }, [fiscalData]);

  useEffect(() => {
    setUiState((draft) => {
      draft.cfdiUseOptions = cfdiUsesResponseToSelectOptionsUi(
        cfdiUsesHook.data
      );
      draft.fiscalRegimeOptions = fiscalRegimensResponseToSelectOptionsUi(
        fiscalRegimesHook.data
      );
    });
  }, [fiscalRegimesHook.data, cfdiUsesHook.data]);

  return {
    uiState,
    actions: {
      onChangeTaxInvoiceRequired,
      onChangedRfc,
      onChangedLegalName,
      onChangedPostalCode,
      onChangedEmail,
      handleFiscalRegimeChange,
      handleCfdiUseChange,
      onClickedCancel,
      onClickedSave,
    },
  };
}
